import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail, Loader2 } from "lucide-react";
import { SOCIAL } from "../site-data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "", company_website: "" });
  const [news, setNews] = useState("");
  const [newsHoney, setNewsHoney] = useState("");
  const [loading, setLoading] = useState(false);
  const [newsLoading, setNewsLoading] = useState(false);

  const renderedAt = useRef(Date.now());
  const newsRenderedAt = useRef(Date.now());

  useEffect(() => {
    renderedAt.current = Date.now();
    newsRenderedAt.current = Date.now();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill all fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        elapsed_ms: Date.now() - renderedAt.current,
      });
      toast.success("Thank you — your note has reached the author.");
      setForm({ name: "", email: "", message: "", company_website: "" });
      renderedAt.current = Date.now();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!news) {
      toast.error("Please enter an email.");
      return;
    }
    setNewsLoading(true);
    try {
      await axios.post(`${API}/newsletter`, {
        email: news,
        company_website: newsHoney,
        elapsed_ms: Date.now() - newsRenderedAt.current,
      });
      toast.success("You're on the list. Watch this space.");
      setNews("");
      setNewsHoney("");
      newsRenderedAt.current = Date.now();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Could not subscribe. Please check your email."
      );
    } finally {
      setNewsLoading(false);
    }
  };

  // Honeypot field style — visually and semantically hidden, but fillable by bots
  const honeypotStyle = {
    position: "absolute",
    left: "-9999px",
    top: "auto",
    width: "1px",
    height: "1px",
    overflow: "hidden",
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 lg:py-36 paper-bg"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <p className="eyebrow mb-5">Say Hello</p>
          <h2
            className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight text-[#1c1a1a]"
            data-testid="contact-title"
          >
            A letter, a line, a <span className="italic text-[#8b2e3e]">kindness</span>.
          </h2>
          <p className="mt-5 text-[#4a4646] text-lg leading-relaxed max-w-md">
            Write to Nitin about the stories, share what moved you, or simply say hello.
            Every note is read.
          </p>

          <div className="mt-10 space-y-4 text-[#1c1a1a]">
            <a
              href={`mailto:${SOCIAL.email}`}
              className="flex items-center gap-3 link-underline"
              data-testid="contact-email-link"
            >
              <Mail size={16} className="text-[#8b2e3e]" />
              {SOCIAL.email}
            </a>
          </div>

          <div className="mt-12 border border-[#e5dcd0] bg-white/60 p-6 max-w-md">
            <p className="eyebrow mb-3">Newsletter</p>
            <p className="text-[#4a4646] text-sm leading-relaxed">
              Occasional notes when a new story arrives. No spam, no noise.
            </p>
            <form onSubmit={subscribe} className="mt-5 flex gap-2" data-testid="newsletter-form">
              <input
                type="email"
                value={news}
                onChange={(e) => setNews(e.target.value)}
                placeholder="your@email.com"
                required
                data-testid="newsletter-input"
                className="flex-1 bg-white border border-[#e5dcd0] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#e5c9c5] focus:border-[#8b2e3e]"
              />
              {/* Honeypot */}
              <label style={honeypotStyle} aria-hidden="true">
                <span>Company website</span>
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={newsHoney}
                  onChange={(e) => setNewsHoney(e.target.value)}
                  name="company_website"
                />
              </label>
              <button
                type="submit"
                disabled={newsLoading}
                data-testid="newsletter-submit"
                className="btn-rose bg-[#1c1a1a] hover:bg-[#8b2e3e] text-white px-4 py-2.5 text-sm tracking-wider transition-colors disabled:opacity-60"
              >
                {newsLoading ? <Loader2 size={16} className="animate-spin" /> : "Subscribe"}
              </button>
            </form>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="bg-white border border-[#e5dcd0] p-8 lg:p-12"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="eyebrow !text-[#4a4646] block mb-2">Name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  required
                  data-testid="contact-input-name"
                  className="w-full border-b border-[#e5dcd0] bg-transparent px-1 py-3 text-lg font-serif text-[#1c1a1a] focus:outline-none focus:border-[#8b2e3e] transition-colors"
                />
              </div>
              <div>
                <label className="eyebrow !text-[#4a4646] block mb-2">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  required
                  data-testid="contact-input-email"
                  className="w-full border-b border-[#e5dcd0] bg-transparent px-1 py-3 text-lg font-serif text-[#1c1a1a] focus:outline-none focus:border-[#8b2e3e] transition-colors"
                />
              </div>
            </div>
            <div className="mt-8">
              <label className="eyebrow !text-[#4a4646] block mb-2">Your note</label>
              <textarea
                rows={6}
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                required
                data-testid="contact-input-message"
                className="w-full border-b border-[#e5dcd0] bg-transparent px-1 py-3 text-base text-[#1c1a1a] focus:outline-none focus:border-[#8b2e3e] transition-colors resize-none"
                placeholder="Share a thought, a story, a kind word…"
              />
            </div>

            {/* Honeypot field (hidden from users, but bots fill it) */}
            <label style={honeypotStyle} aria-hidden="true">
              <span>Company website</span>
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.company_website}
                onChange={(e) => setForm((f) => ({ ...f, company_website: e.target.value }))}
                name="company_website"
              />
            </label>

            <div className="mt-10 flex items-center justify-between gap-4 flex-wrap">
              <p className="text-xs text-[#4a4646] max-w-sm">
                Your email stays private and is used only to reply to your note. Protected from spam — no
                CAPTCHA required.
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="btn-rose inline-flex items-center gap-2 bg-[#8b2e3e] hover:bg-[#6d2230] text-white px-7 py-3.5 text-sm tracking-[0.15em] uppercase transition-colors disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                Send note
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
